import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { environment as env } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, first, take } from 'rxjs';
import { LgvLoanApp } from '../../data-models/lgv-loan-application.model';
import { LgvAuthMgrService } from '../../core/services/lgv-auth-mgr.service';
import { LgvClientService, LgvUserService, NotificationService } from '../../core/core.module';
import { StdObj } from '../../data-models/standard-objects';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AddDocToTaskModalComponent } from '../add-doc-to-task-modal/add-doc-to-task-modal.component';

@Component({
    selector: 'lgv-document-viewer-modal',
    templateUrl: './document-viewer-modal.component.html',
    styleUrls: ['./document-viewer-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentViewerModalComponent implements OnInit, AfterViewInit {

    applicationId: string;
    task: LgvLoanApp.Task;
    vaultDocument: LgvLoanApp.VaultDocument;
    newWindow = false;

    pageMode$ = new BehaviorSubject('default');
    documentName = "";

    rejectReasons: StdObj.ListItem[] = [];
    selectedReason: StdObj.ListItem;
    rejectNotes: string;

    _rejectForm: UntypedFormGroup;

    _pagePurpose: PagePurpose = PagePurpose.Undefined;
    pagePurpose$ = new BehaviorSubject(this._pagePurpose);

    dataParam: any;

    get contentType():string{
        return this.dataParam?.contentType ?? 'application/pdf';
    }

    get isApplicationDocument():boolean {return this.pagePurpose$.value === PagePurpose.ApplicationDocument;}
    get isEmailAttachmentMode():boolean {return this.pagePurpose$.value === PagePurpose.EmailAttachment;}
    get isVaultDocument():boolean {return this.pagePurpose$.value === PagePurpose.VaultDocument;}

    constructor(private http: HttpClient,
        public lgvAuthMgrSvc: LgvAuthMgrService,
        public lgvUserSvc: LgvUserService,
        private lgvClientSvc: LgvClientService,
        private fb: UntypedFormBuilder,
        public notificationSvc: NotificationService,
        public matDialog: MatDialog,
        public dialogRef: MatDialogRef<DocumentViewerModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
    ) {

        this.dataParam = data;
        
        if (data.pagePurpose){
            this.pagePurpose$.next(data.pagePurpose);
        } else if(data.applicationId && data.task){
            this.pagePurpose$.next(PagePurpose.ApplicationDocument);
        } else if(data.email && data.emailAttachment && data.userId){
            this.pagePurpose$.next(PagePurpose.EmailAttachment);
        } else if(data.applicationId && data.vaultDocument){
            this.pagePurpose$.next(PagePurpose.VaultDocument);
        }

        switch(this.pagePurpose$.value){
            case PagePurpose.ApplicationDocument:{
                this.applicationId = data.applicationId;
                // this.taskId = data.taskId;
                // this.documentId = data.documentId;
                this.task = data.task;
        
                this.documentName = this.task?.document?.name;        
                }
                break;
            case PagePurpose.ApplicationDocument:{
                this.documentName = data.attachment.name;
                break;
            }
            case PagePurpose.VaultDocument:{
                this.applicationId = data.applicationId;
                this.vaultDocument = data.vaultDocument;
                this.fetchVaultDocument();
                break;
            }
        }

        if (data.newWindow !== null) {
            this.newWindow = data.newWindow;
        }
    }


    ngOnInit(): void {
        this.getRejectReasons();

        this._rejectForm = this.fb.group({
            reason: [null, [Validators.required]],
            notes: [null, [Validators.required]]
        });
    }

    ngAfterViewInit(): void {
        if(this.pagePurpose$.value === PagePurpose.Undefined){
            console.log('Missing required information to fetch Document');
            return;
        }

        this.fetchDocument();
    }

    onWindowResize(event) {
        this.setIframeSizeFromContainer();
    }

    setIframeSizeFromContainer(){
        let results = document.querySelectorAll("mat-dialog-container");
        if(results && results.length > 0){
            let containerElement = results[0];
            const documentViewer = document.getElementById('documentIFrameViewer');
            const iframeElement = document.getElementById('documentIFrame');
            
            // console.log('');
            // console.log(`C: ${containerElement.clientWidth} x ${containerElement.clientHeight}`  );
            // console.log(`W: ${documentViewer.offsetWidth} x ${documentViewer.offsetHeight}`  );
            // console.log(`I: ${iframeElement.offsetWidth} x ${iframeElement.offsetHeight}`  );
    
            let wrapperDeltaWidth = 75;
            let wrapperDeltaHeight = 175;
            let iFrameDelta = 2;

            if(this.pageMode$.value === 'rejectMode'){
                wrapperDeltaHeight += 140;
            }
    
            documentViewer.style.width = (+containerElement.clientWidth - wrapperDeltaWidth) + 'px';
            documentViewer.style.height = (+containerElement.clientHeight - wrapperDeltaHeight) + 'px';
    
            iframeElement.style.width = (+containerElement.clientWidth - wrapperDeltaWidth - iFrameDelta) + 'px';
            iframeElement.style.height = (+containerElement.clientHeight - wrapperDeltaHeight - iFrameDelta) + 'px';
        }
    }

    fetchDocument() {
        switch(this.pagePurpose$.value){
            case PagePurpose.ApplicationDocument:
                this.fetchApplicationDocument();
                break;

            case PagePurpose.EmailAttachment:
                this.fetchEmailAttachment();
                break;
            
            case PagePurpose.VaultDocument:
                this.fetchVaultDocument();
                break;
        }
    }

    fetchEmailAttachment(){
        let bldUrl = `${env.LgvApiBaseUrl}/users/${this.dataParam.userId}/emails/${this.dataParam.email.id}/attachments/${this.dataParam.emailAttachment.id}`;

        const headers = new HttpHeaders({ 'Content-type': this.contentType });

        this.http.get<Blob>(bldUrl, { headers: headers, responseType: 'blob' as 'json' }).pipe(take(1)).subscribe(x => {
            console.log('have result from document fetch:');
            console.log(x);

            let file = new Blob([x], { type: this.contentType });
            let fileUrl = URL.createObjectURL(file);

            if (this.newWindow) {
                window.open(fileUrl, '_new');
                this.onCancel();
            }
            else {
                const documentViewer = document.getElementById('documentIFrameViewer');
                documentViewer.innerHTML = `<iframe id="documentIFrame" src="${fileUrl}" style="width:100%; height:100%;"></iframe>`;
                setTimeout(() => {
                    const iframe = document.getElementById('documentIFrame') as HTMLIFrameElement;
                    if (iframe && iframe.contentDocument) {
                        let style = document.createElement('style');
                        style.textContent = `
                            body {
                                margin: 0;
                                width: 100%;
                                background: #fff;
                            }
                            img {
                                max-width: 100%;
                                max-height: 100%;
                                object-fit: contain;
                            }
                        `;
                        iframe.contentDocument.head.appendChild(style);
                    }
                    this.setIframeSizeFromContainer();
                }, 500);
            }
        });
    }

    fetchApplicationDocument() {

        const clientQuery = this.lgvAuthMgrSvc.IsClient ? `?clientId=${this.lgvClientSvc.ClientID$.value}` : '';
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${this.applicationId}/tasks/${this.task.id}/documents/${this.task.document.id}${clientQuery}`;

        const headers = new HttpHeaders({ 'Content-type': this.contentType });

        this.http.get<Blob>(bldUrl, { headers: headers, responseType: 'blob' as 'json' }).pipe(take(1)).subscribe(x => {

            let file = new Blob([x], { type: this.contentType });
            let fileUrl = URL.createObjectURL(file);

            if (this.newWindow) {
                window.open(fileUrl, '_new');
                this.onCancel();
            }
            else {
                const documentViewer = document.getElementById('documentIFrameViewer');
                documentViewer.innerHTML = `<iframe id="documentIFrame" src="${fileUrl}" style="width:500px; height:600px;"></iframe>`;
                this.setIframeSizeFromContainer();
            }
        });
    }

    fetchVaultDocument(): void {
        const clientQuery = this.lgvAuthMgrSvc.IsClient ? `?clientId=${this.lgvClientSvc.ClientID$.value}` : '';
        const bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${this.applicationId}/vault/documents/${this.vaultDocument.id}${clientQuery}`;
    
        this.http.get<Blob>(bldUrl, { responseType: 'blob' as 'json' }).pipe(take(1)).subscribe((blob) => {
            const fileUrl = URL.createObjectURL(blob);
    
            if (this.newWindow) {
                window.open(fileUrl, '_new');
                this.onCancel();
            } else {
                const documentViewer = document.getElementById('documentIFrameViewer');
                documentViewer.innerHTML = `<iframe id="documentIFrame" src="${fileUrl}" style="width:500px; height:600px;"></iframe>`;
                this.setIframeSizeFromContainer();
            }
        });
    }    

    getRejectReasons() {
        const clientQuery = this.lgvAuthMgrSvc.IsClient ? `?clientId=${this.lgvClientSvc.ClientID$.value}` : '';
        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/task-rejection-reasons${clientQuery}`;

        this.http.get<any>(bldUrl).pipe(take(1)).subscribe(reasons => {
            if(reasons && reasons.length > 0){
                for(let r of reasons){
                    this.rejectReasons.push({ID: r.id, Display: r.name});
                }
            }
        });
    }

    onSelectReason(reason: StdObj.ListItem){
        this.selectedReason = reason;
    }

    onEditDocumentName() {
        this.pageMode$.next('editDocumentName');
    }

    onSaveDocumentName() {

        if(!this.documentName || this.documentName.trim().length == 0){
            this.notificationSvc.error("Document name is required");
        }



        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${this.applicationId}/tasks/${this.task.id}/documents/${this.task.document.id}`;

        // kxk: todo ===============================
        /* if client, need to add ?clientId= */

        let model = {
            name: this.documentName
        }

        this.http.patch<any>(bldUrl, model).pipe(first()).subscribe(x => {
            this.task.document.name = this.documentName;
            this.notificationSvc.success(`Document name has been updated`);
            this.pageMode$.next('default');
        },
            err => {
                console.log(`Error trying to renamed document`);
                console.log(err);
            }
        );
    }

    onCancelDocumentNameEdit() {
        this.pageMode$.next('default');
    }

    onAcceptDocument() {
        this.updateDocumentStatus('accept');
    }

    onGoToRejectMode() {
        this.pageMode$.next('rejectMode');

        setTimeout(() => {
            this.setIframeSizeFromContainer();
        }, 100);
    }

    onRejectDocument() {
        this.updateDocumentStatus('reject');
    }

    onResetPageMode(){
        this.pageMode$.next('default');

        setTimeout(() => {
            this.setIframeSizeFromContainer();
        }, 100);
    }

    isUploadedStatus() {
        return this.task?.status?.id.toLowerCase() === 'b6c265c4-ffd1-ee11-85f7-6045bd020c58';
    }

    updateDocumentStatus(action: string) {
        // action is "accept" or "reject"

        let model = {};

        if(action === 'accept'){
            model = {
                id: this.applicationId,
                taskId: this.task.id
            }
        }

        let formModel = this._rejectForm.getRawValue();
        if(action === 'reject'){
            model = {
                acceptAndDuplicate: false,
                rejectionReasonNote: formModel.notes,
                rejectionReason:{
                    id: formModel.reason.ID
                }
            }
        }

        let bldUrl = `${env.LgvApiBaseUrl}/loanapplications/${this.applicationId}/tasks/${this.task.id}/${action}`;
        this.http.post<any>(bldUrl, model).pipe(first()).subscribe(x => {

            this.notificationSvc.success(`Document - ${this.task.document.name} has been ${action}ed`);
            this.onFinished();
        },
            err => {
                console.log(`Error trying to ${action} document`);
                console.log(err);
            }
        );
    }

    onFinished(){
        this.dialogRef.close({ action: true });
    }

    onCancel() {
        // return true here incase the name was changed (ie: force parent page to do a reload)
        this.dialogRef.close({ action: true });
    }

    onAddFileToLoan(){
        console.log('add file to loan()');
        const dialogRef = this.matDialog.open(AddDocToTaskModalComponent, {
            width: '500px',
            data: { 
                email:this.dataParam.email,
                emailAttachment: this.dataParam.emailAttachment,
                selectedAgentId:this.dataParam.userId,
                selectedCompanyId:this.dataParam.selectedCompanyId,
                selectedOrganizationId: this.dataParam.selectedOrganizaitonId
            }
        });

    }
}

export enum PagePurpose { 
    Undefined='Undefined',
    ApplicationDocument='ApplicationDocument', 
    EmailAttachment = 'EmailAttachment',
    VaultDocument = 'VaultDocument'
}

